// Foundations
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, fromEvent, Observable, Subscription } from 'rxjs';
import { FormGroup } from '@angular/forms';

// Components
import { PaymentDetailsFormComponent } from '../../_share/payment-details-form/payment-details-form.component';

// Services
import { MyProfileService } from '../../../services/my-profile.service';
import { AnalyticsService } from 'src/app/services/analytics.service';

// Models
import { OrderRequestItem, OrderRequestPriceInfo } from 'src/app/models/package/order-request.model';
import { PurchaseInfo } from 'src/app/models/package/purchase.model';

import {PaymentCreditCard} from '../../../models/payment/payment-card.model';
import {LoadingIndicatorService} from '../../../services/loading-indicator.service';
import {PaymentService} from '../../../services/payment.service';
import {DialogService} from '../../../services/dialog.service';

// import * as lodash from 'lodash';

const ACTIVE_CHECK_PATH = 'assets/svg/ico_check_solid.svg';
const INACTIVE_CHECK_PATH = 'assets/svg/ico_check_solid_white.svg';

@Component({
  selector: 'app-package-checkout',
  templateUrl: './package-checkout.component.html',
  styleUrls: ['./package-checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageCheckoutComponent implements OnInit, OnDestroy {

  // #region -> Property - IO <-
  // ----------

  @Input() orderRequest$: Observable<OrderRequestItem>;
  orderRequest: OrderRequestItem = new OrderRequestItem({});
  orderRequestSubscription: Subscription;

  @Input() purchaseInfo$: Observable<PurchaseInfo>;
  purchaseInfo: PurchaseInfo;
  purchaseInfoSubscription: Subscription;
  @Output() purchaseInfoChange = new EventEmitter<PurchaseInfo>();

  @Input() selectedPaymentCreditCard: PaymentCreditCard;

  @Output() makePurchase = new EventEmitter<any>();
  @Output() changeStep = new EventEmitter<number>();

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Property <-
  // ----------

  @ViewChild('pdForm', { static: false }) pdForm: PaymentDetailsFormComponent;

  orderRequestPriceInfo: OrderRequestPriceInfo = {
    subtotal: 0,
    taxRate: 0,
    taxTotal: 0,
    orderTotal: 0,
    tipTotal: 0,
    total: 0
  };

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    public myProfileService: MyProfileService,
    private changeDetectorRef: ChangeDetectorRef,
    private _analytics: AnalyticsService,
    private paymentService: PaymentService,
    private loadingIndicatorService: LoadingIndicatorService,
    private dialogService: DialogService,

  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngOnInit(): void {
    //
    this.orderRequestSubscription = this.orderRequest$.subscribe(
      (newOrderRequest) => {
        this.orderRequest = this.orderRequest.update({
          ...newOrderRequest.json(),
        });

        //
        this.orderRequestPriceInfo = this.orderRequest.calculatePriceInfo();

        // Trigger change detection when mutated.
        this.changeDetectorRef.markForCheck();
      }
    );

    this.purchaseInfoSubscription = this.purchaseInfo$.subscribe(
      (newPurchaseInfo) => {
        this.purchaseInfo = {...this.purchaseInfo, ...newPurchaseInfo};

        // Trigger change detection when mutated.
        this.changeDetectorRef.markForCheck();
      }
    );
  }

  ngOnDestroy(): void {
    //
    this.orderRequestSubscription.unsubscribe();
    this.purchaseInfoSubscription.unsubscribe();
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - UI <-
  // ----------

  get getTurnRadioPath() {
    return this.purchaseInfo.newCard ? ACTIVE_CHECK_PATH : INACTIVE_CHECK_PATH;
  }

  selectExistingPaymentCard(event: PaymentCreditCard){
    // Update the selection
    this.purchaseInfo = { ...this.purchaseInfo, ...{
      paymentCard: event,
      newCard: false,
      saveNewCard: false,
    }};
    this.purchaseInfoChange.emit(this.purchaseInfo);
  }

  changeExistingPaymentCard() {
    this._analytics.logLinkTap('package_changePaymentMethod');

    // Change to the payment selection page.
    this.changeStep.emit(6);
  }

  handleSelectNewCreditCard() {
    this._analytics.logButtonTap('package_payWithCreditCard');

    // Update the purchase info
    this.purchaseInfo = { ...this.purchaseInfo, ...{
      paymentCard: null,
      newCard: true,
      saveNewCard: false,
    }};
  }

  handleCheckSaveNewCard() {
    // Update the flag
    this.purchaseInfo = { ...this.purchaseInfo, ...{
      saveNewCard: !this.purchaseInfo.saveNewCard,
    }};
    this.changeDetectorRef.markForCheck();
    this.purchaseInfoChange.emit(this.purchaseInfo);

    if (this.purchaseInfo.saveNewCard) {
      this._analytics.logButtonTap('package_saveCreditCardAfterPay');
    }
  }

  canPurchase(): boolean {
    //
    if (this.purchaseInfo.newCard && this.pdForm?.form?.valid) {
      return true;
    }

    if (!this.purchaseInfo.newCard && this.purchaseInfo.paymentCard) {
      return true;
    }
  }

  onPurchase() {
    //
    if (this.purchaseInfo.newCard){
      // after submit this will handled by paymentAddSuccess or paymentAddFailure
      this.pdForm.submit();
    }
    else {
      // Pay with saved card.
      this.makePurchase.emit();
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - TranSafe iFrame <-
  // ----------

  paymentAddFailure(event){
    this.dialogService.showAlert({ content: event });
  }

  /*
   * After we successfully save a payment method we create the order and try to
   * create a payment for it
   */
  paymentAddSuccess(event){
    //
    const formGroup: FormGroup = event as FormGroup;

    const newCardInfo: PaymentCreditCard = {
      cardToken: formGroup.controls.cardToken.value,
      cardholderName: formGroup.controls.cardholderName.value,
      cardNickname: formGroup.controls.cardNickname.value,
      cardType: formGroup.controls.cardType.value,
      // cardExpirationDate: moment().toDate(),
      isPrimary: !this.paymentService.hasPrimaryPaymentCard(),
    };

    // Update the selection
    this.purchaseInfo = { ...this.purchaseInfo, ...{
      paymentCard: newCardInfo,
      newCard: true,
    }};
    this.purchaseInfoChange.emit(this.purchaseInfo);

    // Pay with saved card.
    this.makePurchase.emit();
  }


  back() {
    this._analytics.logButtonTap('package_backFromCheckout');

    this.changeStep.emit(3);
  }


  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------



  // ----------
  // #endregion

  /////////////////////////////


}
